import React, { useCallback, useMemo, useRef } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import {
    firstIndexToFocus,
    listItemKey,
    uniqueRowKeys,
} from 'flow/components/nav/list-keys/list-key-action';
import { useRowStoreHolding } from 'flow/components/nav/list-keys/row-index-store';
import useListTabStop from 'flow/components/nav/list-keys/useListTabStop';
import { ChatConferenceParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';
import {
    ChatListRow,
    chatListRowHeight,
    chatListRowKey,
    rowIsFocusable,
} from './chat-list-rows';
import ChatListWindowRow, { ChatListItemData } from './ChatListWindowRow';

type Props = {
    rows: ChatListRow[];
    /** Shown in place of the list, for a search that matched nothing. */
    emptyState?: React.ReactNode;
};

/** The scrolling half of the chat list - the same whether or not there is
 *  something in the search field. */
const ChatSessionsListView = ({ rows, emptyState }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<VariableSizeList>(null);
    const { id: openChatSessionKey } = useParams<ChatConferenceParams>();
    const meetingsAreOpen = !!useRouteMatch(routes['messages.meetings']);
    // Messages itself opens the Overview too.
    const overviewRouteIsOpen = !!useRouteMatch(routes['messages.overview']);
    const messagesRouteIsOpen = !!useRouteMatch({
        path: routes['messages'],
        exact: true,
    });
    const overviewIsOpen = overviewRouteIsOpen || messagesRouteIsOpen;
    // The key of the open page's row, as chatListRowKey gives it.
    let openRowKey: string | null = openChatSessionKey ?? null;
    if (meetingsAreOpen) {
        openRowKey = chatListRowKey({ kind: 'meetings' });
    } else if (overviewIsOpen) {
        openRowKey = chatListRowKey({ kind: 'overview' });
    }
    // The rows read the open row from this store, so opening a chat
    // renders only the row that closes and the row that opens.
    const openRowStore = useRowStoreHolding(openRowKey);

    // Tab enters at the row of the open page: a chat or the meetings.
    const entryIndex = useCallback(() => {
        const openRowIndex = rows.findIndex(
            (row) => chatListRowKey(row) === openRowKey,
        );
        return openRowIndex === -1
            ? firstIndexToFocus(rows, rowIsFocusable)
            : openRowIndex;
    }, [rows, openRowKey]);

    const rowKeys = useMemo(() => uniqueRowKeys(rows, chatListRowKey), [rows]);
    const {
        tabStopStore,
        onFocus,
        onKeyDown,
        onItemsRendered,
        onRowUnmountWithFocus,
    } = useListTabStop({
        containerRef,
        listRef,
        rows,
        rowKeys,
        rowIsFocusable,
        entryIndex,
    });

    const itemData = useMemo<ChatListItemData>(
        () => ({
            rows,
            tabStopStore,
            rowKeys,
            onRowUnmountWithFocus,
            openRowStore,
        }),
        [rows, tabStopStore, rowKeys, onRowUnmountWithFocus, openRowStore],
    );

    if (listRef.current) {
        // Reset the whole list item height cache here. We could calculate from which index to
        // clear the cache here but that would add extra complexity and will be hard to maintain,
        // so we go with this approach and will optimize it if we see that it's necessary.
        listRef.current.resetAfterIndex(0, false);
    }

    return (
        <div className="flex h-full flex-1 flex-col" data-context-menu="false">
            <div // NOSONAR
                // Not a control: see useListTabStop.
                ref={containerRef}
                className="left-nav-scroll-wrapper h-full ps-3 pb-3"
                onFocus={onFocus}
                onKeyDown={onKeyDown}
            >
                {emptyState ?? (
                    <AutoSizer className="size-full" disableWidth={true}>
                        {({ height }) => (
                            <VariableSizeList
                                ref={listRef}
                                overscanCount={7}
                                height={height}
                                width="100%"
                                itemData={itemData}
                                itemCount={rows.length}
                                itemSize={(index) =>
                                    chatListRowHeight(rows[index])
                                }
                                itemKey={listItemKey}
                                onItemsRendered={onItemsRendered}
                                className="scrollbar left-nav-scroll w-full"
                                style={{
                                    overflowY: 'scroll',
                                    direction: undefined,
                                }}
                            >
                                {ChatListWindowRow}
                            </VariableSizeList>
                        )}
                    </AutoSizer>
                )}
            </div>
        </div>
    );
};

export default ChatSessionsListView;
